/**
 * useItemListPqlCatalog — the PQL vocabulary for item queries.
 *
 * One catalog, two consumers: the statement bar (useStatementQuery) and the
 * ⌘K command bar (CommandPalette). Both must validate against exactly the same
 * fields, aliases and option values, or a statement the command bar accepts
 * could fail to parse when the bar re-reads it from the URL.
 *
 * Built from the same resolved field defs the filter builder uses
 * (config/filterFields.ts), so "add a field to the registry → it exists in
 * PQL" holds with no extra work.
 */

import { useMemo } from 'react';
import { useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';
import { flattenFilterFieldGroups, resolveFilterFieldGroups } from '../config/filterFields';
import { useDynamicFilterOptions } from './useDynamicFilterOptions';
import { buildCatalog, type PqlCatalog } from '../../../../lib/pql';

export function useItemListPqlCatalog(): PqlCatalog {
  const { dynamicOptionsMap, isSubjectTypesEnabled, enabledCustomFields } = useDynamicFilterOptions();
  const currentUserId = useEnterpriseIdentityStore((s) => s.currentUserProfile?.id);

  return useMemo(() => {
    const groups = resolveFilterFieldGroups({
      dynamicOptionsMap,
      customFields: enabledCustomFields,
      includeMetaCategory: isSubjectTypesEnabled,
    });
    return buildCatalog(flattenFilterFieldGroups(groups), {
      ...(currentUserId ? { currentUserId } : {}),
    });
  }, [dynamicOptionsMap, enabledCustomFields, isSubjectTypesEnabled, currentUserId]);
}
